import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PanchTatvaButton from '../components/PanchTatvaButton';
import { useAuth } from '../context/AuthContext';
import { getOrdersByUser } from '../services/orderService';

export default function OrderHistory() {
  const navigate = useNavigate();
  const { userInfo, getIdToken } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch orders from MongoDB backend
  const fetchOrders = useCallback(async () => {
    if (!userInfo?.uid) return;
    setLoading(true);
    try {
      const idToken = await getIdToken();
      const data = await getOrdersByUser(userInfo.uid, idToken);
      const normalized = data.map((order) => ({
        ...order,
        date: order.createdAt instanceof Date ? order.createdAt : new Date(order.createdAt),
        items: Array.isArray(order.items) ? order.items.length : order.items || 0,
        total: order.pricing?.total || 0,
        address: order.shippingAddress
          ? `${order.shippingAddress.city}, ${order.shippingAddress.state}`
          : '',
      }));
      setOrders(normalized);
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError('Failed to load your orders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userInfo?.uid, getIdToken]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Filter and sort orders
  useEffect(() => {
    let filtered = orders;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Apply sorting
    if (sortBy === 'newest') {
      filtered.sort((a, b) => b.date - a.date);
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => a.date - b.date);
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => b.total - a.total);
    } else if (sortBy === 'price-low') {
      filtered.sort((a, b) => a.total - b.total);
    }

    setFilteredOrders(filtered);
  }, [orders, statusFilter, sortBy]);

  const getStatusColor = status => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'delivered':
        return '✓';
      case 'shipped':
        return '→';
      case 'processing':
        return '⏳';
      case 'cancelled':
        return '✕';
      default:
        return '•';
    }
  };

  const formatDate = date => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20 text-center">
        <p className="text-bodyText text-lg">Loading your orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20 text-center">
        <p className="text-red-500 mb-6">{error}</p>
        <PanchTatvaButton onClick={() => window.location.reload()}>Try Again</PanchTatvaButton>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20 text-center">
        <h1 className="text-3xl md:text-4xl font-heading text-headingText mb-6">My Orders</h1>
        <p className="text-bodyText text-lg mb-8">You haven't placed any orders yet.</p>
        <PanchTatvaButton onClick={() => navigate('/shop')}>
          Start Shopping
        </PanchTatvaButton>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20">
      <h1 className="text-3xl md:text-4xl font-heading text-headingText mb-8">My Orders</h1>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div>
          <label className="block text-sm font-semibold text-bodyText mb-2">Filter by Status</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border-2 border-gray-300 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary"
          >
            <option value="all">All Orders</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-bodyText mb-2">Sort by</label>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border-2 border-gray-300 bg-primaryBg text-bodyText focus:outline-none focus:border-secondary"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="price-high">Price: High to Low</option>
            <option value="price-low">Price: Low to High</option>
          </select>
        </div>
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-bodyText text-lg">No orders found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map(order => (
            <div
              key={order.id}
              className="bg-cardBg rounded-lg p-4 md:p-6 shadow-lg hover:shadow-xl transition"
            >
              {/* Mobile: stacked layout; Desktop: row */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                {/* Order ID + Date */}
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Order ID</p>
                  <p className="font-semibold text-bodyText text-sm break-all">{order.id}</p>
                  <p className="text-xs text-gray-500">{formatDate(order.date)}</p>
                </div>

                {/* Status Badge */}
                <span
                  className={`self-start sm:self-auto inline-block px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(order.status)}`}
                >
                  {getStatusIcon(order.status)} {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-6 gap-1">
                  <div className="text-sm text-bodyText">
                    <span className="text-gray-500">Items: </span>
                    <strong>{order.items}</strong>
                  </div>
                  <div className="text-sm text-bodyText">
                    <span className="text-gray-500">Total: </span>
                    <strong className="text-secondary">₹{order.total}</strong>
                  </div>
                  <div className="text-sm text-bodyText">
                    <span className="text-gray-500">Ship to: </span>
                    <strong>{order.address}</strong>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/orders/${order.id}`)}
                  className="ml-4 px-4 py-2 rounded-lg bg-secondary text-primaryBg font-semibold text-sm hover:opacity-90 transition whitespace-nowrap"
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
